import { FormEvent, useState } from 'react';
import { useAuth } from '../lib/auth';
import { ApiError } from '../lib/api';
import { Alert, Button, Field, Input, Wordmark } from '../components/ui';

type Mode = 'login' | 'signup';

export default function AuthPage() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<Mode>('login');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (mode === 'login') {
        await login(email.trim(), password);
      } else {
        await register({ email: email.trim(), password, fullName: fullName.trim() || undefined });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-full lg:grid-cols-2">
      {/* Left: brand panel (hidden on small screens) */}
      <aside className="relative hidden overflow-hidden border-r border-line p-12 lg:flex lg:flex-col lg:justify-between">
        <Wordmark className="text-2xl" />
        <div>
          <h1 className="h-display max-w-md text-5xl leading-[0.95] text-soft">
            Your competitor is <span className="text-power">getting leads</span> right now.
          </h1>
          <p className="mt-5 max-w-sm text-muted">
            Sourcing, calls, walk-ins, email, and a CRM that fills its own timeline — one place,
            one pipeline. Built for one seller or a whole team.
          </p>
        </div>
        <p className="text-xs text-muted">More Than Momentum · Outreach Platform</p>
      </aside>

      {/* Right: form */}
      <main className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <Wordmark />
          </div>

          <h2 className="h-display text-3xl text-soft">
            {mode === 'login' ? 'Sign in' : 'Create your workspace'}
          </h2>
          <p className="mt-1 text-sm text-muted">
            {mode === 'login'
              ? 'Welcome back.'
              : 'Starts as a workspace of one — invite your team any time.'}
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            {mode === 'signup' && (
              <Field label="Your name">
                <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Colby Smith" autoComplete="name" />
              </Field>
            )}
            <Field label="Email">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoComplete="email"
              />
            </Field>
            <Field label="Password" hint={mode === 'signup' ? 'At least 8 characters.' : undefined}>
              <Input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              />
            </Field>

            {error && <Alert>{error}</Alert>}

            <Button type="submit" loading={busy} className="w-full">
              {mode === 'login' ? 'Sign in' : 'Create workspace'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted">
            {mode === 'login' ? "Don't have a workspace?" : 'Already have one?'}{' '}
            <button
              className="font-semibold text-electric hover:underline"
              onClick={() => {
                setMode(mode === 'login' ? 'signup' : 'login');
                setError(null);
              }}
            >
              {mode === 'login' ? 'Create one' : 'Sign in'}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
